// External UI Dependencies
import { HeroUIProvider } from '@heroui/react';
import { Bounce, ToastContainer } from 'react-toastify';

// External Theme Dependencies
import { ThemeProvider as NextThemesProvider } from 'next-themes';

// React Types
import type { ReactNode } from 'react';
import { useEffect } from 'react';

// Theme utilities
import { initializeTheme } from '@/lib/utils/theme';

/**
 * Interface defining the required router functionality.
 * This provides a consistent routing interface regardless of the underlying router implementation.
 */
interface IRouter {
    /** Navigate to a new path */
    push: (path: string) => void;
    /** Replace current route without adding to history */
    replace?: (path: string) => void;
    /** Pre-fetch a route for faster navigation */
    prefetch?: () => Promise<void>;
    /** Navigate to previous route */
    back?: () => void;
    /** Navigate to next route if available */
    forward?: () => void;
    /** Refresh current route */
    refresh?: () => void;
}

/**
 * Props type definition for the UIProvider component
 */
type NavigateProps = {
    /** Child components to be wrapped by the providers */
    children: ReactNode;
    /** Router implementation for navigation */
    navigate: IRouter;
    /** Optional forced theme */
    forcedTheme?: string;
};

/**
 * UIProvider Component
 *
 * This component composes NextUI and theme providers to handle:
 * 1. Theme management (light/dark/system)
 * 2. UI component styling and functionality
 * 3. Navigation integration
 *
 * @param {NavigateProps} props - Component properties
 * @param {ReactNode} props.children - Child components to be wrapped
 * @param {IRouter} props.navigate - Router implementation for navigation
 * @param {string} props.forcedTheme - Optional forced theme
 * @returns {JSX.Element} Composed provider wrapper
 */
export const UIProvider = ({ children, navigate, forcedTheme }: NavigateProps) => {
    // Initialize theme on mount
    useEffect(() => {
        initializeTheme();
    }, []);

    return (
        <NextThemesProvider
            attribute="class"
            defaultTheme="light"
            enableSystem={true}
            disableTransitionOnChange={false}
            themes={['light', 'dark']}
            forcedTheme={forcedTheme}
            storageKey="theme-preference"
        >
            <HeroUIProvider navigate={navigate.push}>
                {children}
                <ToastContainer
                    position="bottom-right"
                    theme={forcedTheme === 'dark' ? 'dark' : 'light'}
                    transition={Bounce}
                />
            </HeroUIProvider>
        </NextThemesProvider>
    );
};

// Set display name for debugging in React DevTools
UIProvider.displayName = 'UIProvider';
